import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { DataSourceBase } from "abweb/dto/generated/ds/DataSourceBase";
import { DataSourceRecord } from "abweb/dto/ds/DataSourceRecord";
import { DataSourceState } from "abweb/dto/generated/ds/DataSourceState";
import { LogUtils } from "abweb/utils/log/LogUtils";
import "@abinitio/kendo.all";
import { ClassUtils } from "abweb/utils/ClassUtils";
export class DataSource extends DataSourceBase {
  static createDataSource(records, recordFormat, clientRequestContext = null, argumentValues = null, hierarchical = false) {
    const retVal = new DataSource();
    retVal.initDataSource(argumentValues, clientRequestContext, null, hierarchical, recordFormat, records);
    retVal.recordFormat.setUpFields(retVal.records, retVal);
    return retVal;
  }
  constructor() {
    super();
    _defineProperty(this, "_observableRecords", null);
    /**
     * Indicates if the DataSource has any locally changed values.
     * Please do not set this directly. It should be set only by the internal workings of this class.
     * Its setter is public because of Kendo voodoo.
     * @type {boolean}
     */
    _defineProperty(this, "hasLocalChange", false);
    _defineProperty(this, "ignoreRecordChangeNotification", false);
  }

  /* override */
  initFromJson(json, deserializeContext) {
    super.initFromJson(json, deserializeContext);
    if (this.recordFormat != null && this.records != null) {
      this.recordFormat.setUpFields(this.records, this);
    } else {
      DataSource.LOG.warn("initFromJson: null recordFormat or records");
    }
  }
  initDataSource(argumentValues, clientRequestContext, id, hierarchical, recordFormat, records) {
    super.initDataSource(argumentValues, clientRequestContext, id, hierarchical, recordFormat, records);
    this.initObservableRecords();
  }
  toTSOG(result, serializeContext) {
    /* Have to override this because the base class uses _records which is not in sync with _observableRecords.
    So we hack around this by temporarily setting _records = _observableRecords for the serialization pass.
     */
    const temp = super.records;
    try {
      super.records = this._observableRecords;
      const retVal = super.toTSOG(result, serializeContext);
      return retVal;
    } finally {
      super.records = temp;
    }
  }
  initObservableRecords() {
    if (this._observableRecords != null) {
      this._observableRecords.unbind("change");
    }
    this._observableRecords = super.records == null ? null : new kendo.data.ObservableArray(super.records);
    if (this._observableRecords != null) {
      this._observableRecords.bind("change", e => this.recordsChangeHandler(e));
    }
  }
  get records()
  // this type declaration is wrong - the actual type is: kendo.data.ObservableArray<DataSourceRecord | null> | null
  {
    return this._observableRecords; // fixme - the <any> is hiding a possible null !
  }
  set records(value) {
    super.records = value;
    this.initObservableRecords();
  }
  recordsChangeHandler(event) {
    DataSource.LOG.debug(`recordsChangeHandler event.kind = ${event.action}`);
    if (event.action === "add" || event.action === DataSource.CHILD_RECORDS_ADD) {
      this.processAddedRecords(event.items);
    } else if (event.action === "remove" || event.action === DataSource.CHILD_RECORDS_REMOVE) {
      this.processRemovedRecords(event.items);
    }
  }

  // This code is more complex than expected because of the difference in types between records & _records
  setClientSideFilter(filter) {
    let records;
    if (this.filterFunction) {
      if (filter) {
        this.filterFunction = filter;
        records = this._unfilteredRecords.filter(filter); // apply new filter to original data
      } else {
        // have applied filter before
        if (this._unfilteredRecords) {
          // we can't just set the records back because the types are different!
          records = this._unfilteredRecords.filter(x => x); // we have to filter out possible nulls in the array to fulfil the type contract
        } else {
          records = [];
        }
        this.filterFunction = filter;
        this._unfilteredRecords = null;
      }
    } else {
      if (filter) {
        this.filterFunction = filter;
        this._unfilteredRecords = this.records.slice(); // save off raw (not observable) array
        records = this.records.filter(filter);
      }
    }
    if (records !== undefined) {
      this.replaceRecords(records);
    }
  }
  get hasClientSideFilter() {
    return this.filterFunction != null;
  }
  processAddedRecords(items) {
    // The only thing we care to do here is check for the hasLocalChange flag
    for (const rec of items) {
      if (rec.hasLocalChange) {
        // Note that we only have to process 1 record that has this flag set
        this.onRecordHasLocalChangeChanged(rec);
        return;
      }
    }
  }
  processRemovedRecords(items) {
    if (this.ignoreRecordChangeNotification) {
      return;
    }

    // The only thing we care to do here is check for the hasLocalChange flag
    for (const rec of items) {
      if (rec.hasLocalChange) {
        // Note that we only have to process 1 record that has this flag set
        // We call this because we need to recalc whether or not we have a local change based on the remaining records we have
        this.updateHasLocalChangeValue();
        return;
      }
    }
  }

  /**
   * Finds a top-level record index by id.
   * @param id Record id
   * @returns {number} Zero-based record index into the records array
   * or -1 if not found.
   */
  findRecordIndexById(id) {
    if (!this.records) {
      return -1;
    }
    for (let n = 0; n < this.records.length; n++) {
      if (this.records[n].id === id) {
        return n;
      }
    }
    return -1;
  }

  /**
   * Finds a top-level record index by uid.
   * @param uid Record uid
   * @returns {number} Zero-based record index into the records array
   * or -1 if not found.
   */
  findRecordIndexByUid(uid) {
    if (!this.records) {
      return -1;
    }
    for (let n = 0; n < this.records.length; n++) {
      if (this.records[n].uid === uid) {
        return n;
      }
    }
    return -1;
  }
  findRecordById(id) {
    if (!this.records) {
      return null;
    }
    for (const record of this.records) {
      const target = record.findRecordById(id);
      if (target != null) {
        return target;
      }
    }
    return null;
  }
  findRecordByClientTag(tag) {
    if (!this.records) {
      return null;
    }
    for (const record of this.records) {
      const target = record.findRecordByClientTag(tag);
      if (target != null) {
        return target;
      }
    }
    return null;
  }
  throwIfUndefined(prefix, attr) {
    if (this[attr] == null) {
      throw new Error(`${prefix}: ${attr} is undefined`);
    }
  }

  /**
   * Determines if this DataSource has hierarchical content.
   * NOTE: This is mapped to the hierarchical property which is now part of the DTO.
   */
  get hasHierarchicalContent() {
    // DG-809: The notion of whether a DataSource is hierarchical is now supported by the DTO as the hierarchical property
    // and is directly settable either by server code or in the client.
    return this.hierarchical == null ? false : this.hierarchical;

    // This was the original implementation which walked the records to find out if ANY record has child records
    // for (let rec of this.records)
    // {
    //   if (rec.childRecords != null)
    //   {
    //     return true;
    //   }
    // }
    //
    // return false;
  }

  /**
   * Helper function that VDL authors can use in expressions to tell if we have data or not.
   * @returns {boolean}
   */
  hasRecords() {
    return this.records != null && this.records.length > 0;
  }

  /**
   * Notification from a contained record that its hasLocalChange value has changed.
   * We update our local value of hasLocalChange based on this change.
   * @param rec
   * @returns {boolean}
   */
  onRecordHasLocalChangeChanged(rec) {
    if (this.ignoreRecordChangeNotification) {
      return this.hasLocalChange;
    }
    if (rec.hasLocalChange) {
      this.set("hasLocalChange", true);
      return true;
    }

    // Value has transitioned from true to false so we have to check all values
    this.updateHasLocalChangeValue();
    return this.hasLocalChange;
  }

  /**
   * Notification from a contained DataSourceValue that its currentValue has changed.
   * This notification is used to fire a change event on this DataSource. We do this to address PR 122870
   * which identifies an issue with the way AbTemplateComponent deals with PropertyUpdaters with AbBindModel
   * properties. It only listens for events on the object that is in the BindModel. Since in the 90% case, this
   * is a DataSource, by firing a change event here, we can activate the PropertyUpdaters. Although this change
   * may mean more PropertyUpdaters are called, at least there is a higher chance that binding values will
   * be more up to date.
   * @param source
   */
  onContainedValueChanged(source) {
    this.trigger("change", {
      containedValue: source
    });
  }
  updateHasLocalChangeValue() {
    let newValue = false;
    if (this.records) {
      for (const rec of this.records) {
        if (this.shouldSetHasLocalChange(rec)) {
          newValue = true;
          break;
        }
      }
    }
    this.set("hasLocalChange", newValue);
  }
  shouldSetHasLocalChange(rec) {
    if (rec.hasLocalChange) {
      return true;
    }

    // Check children
    if (rec.childRecords) {
      for (const childRec of rec.childRecords) {
        if (childRec != null && this.shouldSetHasLocalChange(childRec)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Returns Array of all DataSourceRecords that have their hasLocalChange flag set.
   * @returns {Array<DataSourceRecord>}
   */
  getAllChangedRecords() {
    const recs = [];
    if (!this.hasLocalChange) {
      return recs;
    }
    if (this.hasHierarchicalContent) {
      return this.getAllChangedHierarchicalRecords();
    }
    if (!this.records) {
      return [];
    }
    for (const rec of this.records) {
      if (rec.hasLocalChange) {
        rec.prepareValueForSave();
        recs.push(rec);
      }
    }
    return recs;
  }

  /**
   * Called prior to a save operation, this method is called only for hierarchical data.
   * It returns a (sort of) flattened Array of modified records that need to be saved.
   * The records are partial clones because we don't send back any childRecords (unless the childRecords are DELETEd/ADDed in which
   * case we need the parent record to provide context).
   */
  getAllChangedHierarchicalRecords() {
    // This is the output Array of DataSourceRecord "save clones"
    const saveRecords = [];

    // List of source records marked as ADD/DELETE. These are processed in a 2nd pass.
    const addDeleteRecords = [];

    // Hash table Key: source DataSourceRecord that corresponds to value, Value: save clone record in saveRecords
    // This is used when processing addDeleteRecords
    const saveRecordsMap = new Map();
    if (this.records) {
      for (const rec of this.records) {
        processRecord(rec);
      }
    }

    // saveRecords should now have all CHANGE records
    // Now process addDeleteRecords
    for (const rec of addDeleteRecords) {
      const saveClone = rec.createSaveClone();
      saveRecordsMap.set(rec, saveClone);
      if (rec.parentRecord == null) {
        // Top-level record has no parent
        saveRecords.push(saveClone);
      } else {
        let parentSaveClone = saveRecordsMap.get(rec.parentRecord);
        if (parentSaveClone == null) {
          // parentRecord is not marked as CHANGE, so we need to create one
          parentSaveClone = rec.parentRecord.createSaveClone();
          saveRecordsMap.set(rec.parentRecord, parentSaveClone);

          // and put it in the list
          saveRecords.push(parentSaveClone);
        }
        if (parentSaveClone.childRecords) {
          parentSaveClone.childRecords.push(saveClone);
        }
      }
    }
    return saveRecords;
    function processRecord(rec) {
      if (rec.hasLocalChange) {
        if (rec.isAddedLocal || rec.isDeletedLocal || rec.isReAddedLocal || rec.isReparented) {
          // These are dealt with in a 2nd pass
          addDeleteRecords.push(rec);
        } else {
          const saveClone = rec.createSaveClone();
          saveRecords.push(saveClone);
          saveRecordsMap.set(rec, saveClone);
        }
      }
      if (!rec.isDeletedLocal && rec.hasChildRecords) {
        // Descend and flatten
        // Note that if rec is deleted, we don't have to descend because cascade delete will take care of that
        for (const childRec of rec.childRecords) {
          if (childRec) {
            processRecord(childRec);
          }
        }
      }
    }
  }

  /**
   * Determines if any contained cells have validation errors or not.
   * Returns true if all cells are valid, false otherwise.
   */
  isValid() {
    if (this.records != null) {
      for (const rec of this.records) {
        if (!this.isValidHelper(rec)) {
          return false;
        }
      }
    }
    return true;
  }
  isValidHelper(rec) {
    if (!rec.isValid()) {
      return false;
    }
    if (rec.hasChildRecords) {
      for (const childRec of rec.childRecords) {
        if (childRec && !childRec.isValid()) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Appends a record.
   * @param rec
   */
  appendRecord(rec) {
    this.insertRecord(rec);
  }
  /**
   * Inserts a record.
   * @param rec
   * @param index Zero-based insertion index.
   * Default = -1 which is append.
   * @param replace If true and an index >= is specified, this is a replace record operation.
   * NB: I have fears that the replace implementation may not play well with UI component data binding. Our current needs are basic
   * reconciling of saveData() changes for Action.ArgValue resolution. If we need to support UI data binding, we probably
   * have more work to do here.
   */
  insertRecord(rec, index = -1, replace = false) {
    rec._dataSource = this;
    if (this.recordFormat == null) {
      DataSource.LOG.warn(`insertRecord DataSource.id: ${this.id} recordFormat does not exist in DataSource`);
    } else {
      this.recordFormat.setUpFields([rec], this);
    }

    // Kendo notifications?
    if (index < 0) {
      this.records.push(rec);
    } else {
      const deleteCount = replace ? 1 : 0;
      this.records.splice(index, deleteCount, rec);
    }
  }

  /**
   * Merges contents of a sourceRec into a targetRec.
   * The current implementation does a replace of the record.
   * NB: I have fears that this implementation may not play well with UI component data binding. Our current needs are basic
   * reconciling of saveData() changes for Action.ArgValue resolution. If we need to support UI data binding, we probably
   * have more work to do here.
   * @param targetRec Target record in this records collection.
   * @param sourceRec Source record to merge.
   */
  mergeRecord(targetRec, sourceRec) {
    const index = this.records.indexOf(targetRec);
    if (index < 0) {
      return;
    }
    this.insertRecord(sourceRec, index, true);
  }

  /**
   * Creates top-level record of matching type.
   * Does not add the record to the records collection.
   */
  createRecord() {
    const rec = DataSourceRecord.createWithDefaults(null, {});

    // Note that we need to set the _dataSource here because otherwise, the rec won't have access to recordFormat info
    rec._dataSource = this;
    rec.localState = DataSourceState.ADD;
    rec.hasLocalChange = true;
    if (this.recordFormat) {
      for (const fieldName of Object.keys(this.recordFormat.fields)) {
        const targetField = this.recordFormat.findField(fieldName);
        if (targetField != null && (targetField.editable || targetField.editableIfNullOnly || targetField.defaultValue != null)) {
          // Install a default value here
          const dsv = rec.setDataSourceValue(fieldName, targetField.defaultValue);
          dsv._field = targetField;
          dsv._containerRecord = rec;
          if (targetField.editableIfNullOnly) {
            // We have to make sure this field is editable for a newly created record,
            // regardless of whether targetField.editable
            dsv.editableOverwrite = true;
          }
        }
      }
    }
    return rec;
  }

  /**
   * Inserts/reparents records.
   * If you use this as a reparent, all items must have the same parentRecord or an Error will be thrown.
   * @param startIndex Zero-based start index in the records Array of where the insertion should be done.
   * Pass -1 to append.
   * @param items Array of DataSourceRecords to insert.
   * Note that that if reparenting, all items parentRecord must be the same.
   * Note that this API does not (yet) work for a move operation where the items are also top-level records.
   */
  insertRecords(startIndex, items) {
    if (items == null || items.length === 0) {
      return;
    }
    if (items[0].parentRecord == null) {
      throw new Error("insertRecords does not support moving top-level records");
    }
    const originalParentRecord = items[0].parentRecord;
    if (originalParentRecord != null) {
      // Use internal API to remove them first
      const r = originalParentRecord;
      r.removeChildItemsBeforeMove(items);
    }
    if (startIndex < 0) {
      startIndex = this.records.length;
    }
    const args = [startIndex, 0, ...items];

    // Make sure the item fields are properly set
    for (const item of items) {
      // Note that if item._dataSource !== this coming in, it means it came from somewhere else. (How to handle this?)
      item._dataSource = this;
      const r = item;
      if (!item.isAddedLocal && r._originalParentRecord === undefined) {
        // Save this for revert
        r._originalParentRecord = item.parentRecord;
      }
      item._parentRecord = null;
      if (!item.isAddedLocal) {
        if (r._originalParentRecord == null) {
          // We reparented back to the original one,
          // so we need to be more careful about setting localState
          r.updateLocalStateValue(); // Need to check contained values
        } else {
          item.set("localState", DataSourceState.CHANGE);
        }
      }
    }
    kendo.data.ObservableArray.prototype.splice.apply(this._observableRecords, args);
  }
  replaceRecords(records) {
    const args = [0, this.records.length, ...records];
    kendo.data.ObservableArray.prototype.splice.apply(this._observableRecords, args);
  }
  /**
   * Removes a record.
   * @param arg Zero-based index of a top-level record,
   * or a record id or record instance at any level.
   * @returns {any} Record removed or null if not found.
   */
  removeRecord(arg) {
    let index = -1;
    let rec = null;
    if (typeof arg === "number") {
      index = arg;
      if (index < 0 || index >= this.records.length) {
        // Out of bounds
        return null;
      }
    } else if (typeof arg === "string") {
      // Find rec by id
      rec = this.findRecordById(arg);
      if (rec == null) {
        return null;
      }
    } else if (ClassUtils.isInstanceOf(arg, DataSourceRecord)) {
      rec = arg;
    }
    if (rec != null) {
      if (rec._parentRecord == null) {
        // This is a top-level record
        index = this.records.indexOf(rec);
      } else {
        // This is a child record so delegate
        if (rec.isReparented) {
          // Put it back where it was before
          if (rec.originalParentRecord == null) {
            // Originally a top-level record so add it back in...
            this.insertRecords(-1, [rec]);

            // ...and then re-enter after reparenting
            return this.removeRecord(rec);
          }
          rec.originalParentRecord.insertChildRecords(-1, [rec]);
        }
        rec._parentRecord.removeChildRecord(rec);
        return rec;
      }
    }
    if (index < 0) {
      return null;
    }
    rec = this.records[index];

    // Figure out how to deal with this
    if (rec.isAddedLocal) {
      // We can remove this item
      this.records.splice(index, 1);
    } else {
      // Mark it as removed
      rec.markForDelete();
    }
    return rec;
  }

  /**
   * Notification from a record that new records have been added to its childRecords.
   * This method dispatches a change notification on the records ObservableArray with event.action = "childRecordsAdd"
   * @param parentRec Target record
   * @param startIndex Start index of where items were inserted.
   * @param items Items inserted.
   */
  onRecordChildRecordsAdded(parentRec, startIndex, items) {
    if (this._observableRecords) {
      this._observableRecords.trigger("change", {
        action: DataSource.CHILD_RECORDS_ADD,
        parentRec: parentRec,
        index: startIndex,
        items: items
      });
    }
  }

  /**
   * Notification from a record that new records have been removed from its childRecords.
   * This method dispatches a change notification on the records ObservableArray with event.action = "childRecordsRemove"
   * @param parentRec Target record
   * @param startIndex Start index of where items were inserted.
   * @param items Items inserted.
   */
  onRecordChildRecordsRemoved(parentRec, startIndex, items) {
    // When we remove a record, we have to walk the whole set of records to see what our hasLocalChange value should be
    // so optimize this to do it only once at the end
    this.ignoreRecordChangeNotification = true;
    if (this._observableRecords) {
      this._observableRecords.trigger("change", {
        action: DataSource.CHILD_RECORDS_REMOVE,
        parentRec: parentRec,
        index: startIndex,
        items: items
      });
    }
    this.ignoreRecordChangeNotification = false;
    this.updateHasLocalChangeValue();
  }

  /**
   * Reverts all local changes on this DataSource.
   */
  revertLocalChanges() {
    if (this.records == null) {
      return;
    }
    try {
      // While we are looping here, ignore calls to onRecordHasLocalChangeChanged() since they would lead to
      // expensive walks
      this.ignoreRecordChangeNotification = true;
      for (let n = this.records.length - 1; n >= 0; n--) {
        const rec = this.records[n];
        if (rec.isAddedLocal) {
          // Special case where we need to explicitly remove the record here
          this.removeRecord(n);
        } else {
          rec.revertLocalChangeAndDescend();
        }
      }
    } finally {
      this.ignoreRecordChangeNotification = false;
      this.set("hasLocalChange", false);
    }
  }

  /**
   * Sorts the records by a particular field.
   * @param fieldPath Path to field.
   * @param asc true to sort ascending, false to sort descending.
   */
  sortOnField(fieldPath, asc) {
    if (this.recordFormat == null) {
      return;
    }
    if (this.recordFormat.findField(fieldPath) == null) {
      DataSource.LOG.warn(`sort(${fieldPath}) not found`);
      return;
    }
    let recs = this._observableRecords;
    if (recs == null) {
      return;
    }
    let dir = asc ? 1 : -1;
    let records = this.records;

    // Create a Map to record current record indices to provide a stable sort for equal values
    let indexMap = new Map();
    for (let n = 0; n < records.length; n++) {
      indexMap.set(records[n], n);
    }
    records.sort((arec, brec) => {
      let afield = arec.getFieldValue(fieldPath);
      let bfield = brec.getFieldValue(fieldPath);
      if (afield == null && bfield == null) {
        return dir * stableRetValue(arec, brec);
      }
      if (afield == null) {
        // null goes at front
        return -dir;
      } else if (bfield == null) {
        return dir;
      }
      let ret = afield.compare(bfield);
      if (ret === 0) {
        return dir * stableRetValue(arec, brec);
      }
      return dir * ret;
    });
    recs.trigger("change");

    /**
     * This returns a stable sort value based on index.
     * @param arec
     * @param brec
     * @returns {number}
     */
    function stableRetValue(arec, brec) {
      if (arec === brec) {
        return 0;
      }
      return indexMap.get(arec) - indexMap.get(brec);
    }
  }
}
_defineProperty(DataSource, "CHILD_RECORDS_ADD", "childRecordsAdd");
_defineProperty(DataSource, "CHILD_RECORDS_REMOVE", "childRecordsRemove");
_defineProperty(DataSource, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=DataSource.js.map
